import { useCallback, useState } from 'react'
import { Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useBuildStorageLayout } from '@/hooks/useStorage'
import { gridCellLabels, type GridCellLabel } from '@/lib/slotLabels'
import { CopyLayoutPicker } from './CopyLayoutPicker'
import { MatrixPreview } from './MatrixPreview'
import { LabelSchemePicker } from './LabelSchemePicker'
import {
  childKindOptions,
  defaultLevelFor,
  kindWord,
  levelCount,
  parseSectionNames,
  planBlueprint,
  planStoredTotal,
  planTotals,
  sampleAddress,
  toNodeKind,
  validateLevel,
  dimensionProblem,
  validatePlan,
  MAX_LAYOUT_NODES,
  type BlueprintRow,
  type GridLevel,
  type WizardKind,
  type WizardLevel,
} from '@/lib/storageWizard'
import type { ApiStorageNode } from '@/types/storage'

/**
 * Guided setup for a storage unit.
 *
 * One question per screen, in the words a lab uses — "what goes inside each
 * rack?" — with a running picture of the unit above it. That picture is the
 * point of the layout: the thing being described is nested, and answering one
 * question at a time hides its shape, so after three answers you could see
 * that you were three screens in but not what you had said.
 *
 * Each level applies to every node the level above created, which is what
 * makes this quicker than declaring grids by hand: "each rack holds 10 boxes"
 * is answered once, not once per rack. The blueprint states that relationship
 * in both directions — per parent, and what it multiplies to — because that
 * multiplication is the part people get wrong.
 */
export function StorageWizard({
  root,
  onDone,
  onCancel,
  extending = false,
  embedded = false,
}: {
  root: ApiStorageNode
  onDone: () => void
  onCancel: () => void
  /** True when the unit already has levels — re-running only fills in gaps. */
  extending?: boolean
  /**
   * Rendered inside a dialog that already shows the title and subtitle, so the
   * wizard drops its own header rather than repeating them.
   */
  embedded?: boolean
}) {
  // One entry per described level; the last entry is the one being edited.
  const [levels, setLevels] = useState<WizardLevel[]>([defaultLevelFor('RACK')])
  const [reviewing, setReviewing] = useState(false)
  const build = useBuildStorageLayout()

  /**
   * A copied source replaces the plan wholesale and jumps to the review.
   *
   * Landing on the review is the point of copying: the person picked a unit
   * they already trust, so the useful next screen is "here is what that comes
   * to, build it" — not the first of four questions they have just answered
   * by proxy. Every level is still editable from the blueprint.
   */
  const applyCopiedLevels = useCallback((copied: WizardLevel[]) => {
    if (copied.length === 0) return
    setLevels(copied)
    setReviewing(true)
  }, [])

  const index = levels.length - 1
  const current = levels[index]
  const parentKind: WizardKind | null = index === 0 ? null : levels[index - 1].kind
  const unitLabel = root.name ?? root.code
  const parentLabel = index === 0 ? unitLabel : `each ${kindWord(levels[index - 1].kind, 1)}`
  const problem = validateLevel(current, index + 1)
  const canGoDeeper = childKindOptions(current.kind).length > 0

  const setCurrent = (next: WizardLevel) =>
    setLevels((prev) => prev.map((l, i) => (i === index ? next : l)))

  const totals = planTotals(levels)
  const rows = planBlueprint(levels)
  const address = sampleAddress(levels, root.code)
  // The whole plan, not just the level in hand. Each level multiplies by every
  // node the one above produced, so three reasonable numbers can still come to
  // millions — and the server refuses that outright, having written nothing.
  const planProblem = validatePlan(levels)
  const stored = planStoredTotal(levels)

  /** What the unit will actually hold, which is the number people came for. */
  const deepest = totals[totals.length - 1]
  const holds = deepest ? `${deepest.total.toLocaleString()} ${kindWord(deepest.kind, deepest.total)}` : ''

  const goToLevel = (i: number) => {
    setReviewing(false)
    setLevels((prev) => prev.slice(0, i + 1))
  }

  if (reviewing) {
    return (
      <div className='space-y-5'>
        {!embedded && (
          <StepHeader
            title={`Ready to build ${unitLabel}`}
            subtitle='Nothing has been created yet.'
          />
        )}

        <Blueprint
          unitLabel={unitLabel}
          rows={rows}
          address={address}
          stored={stored}
          onGoTo={goToLevel}
        />

        <div className='space-y-2'>
          {rows.map((row, i) => (
            <div key={i} className='rounded-md border p-3'>
              <p className='text-sm font-medium'>
                {row.total.toLocaleString()} {kindWord(row.kind, row.total)}
                {i > 0 && (
                  <span className='font-normal text-muted-foreground'>
                    {' '}
                    — {row.perParent} in each {kindWord(rows[i - 1].kind, 1)}
                  </span>
                )}
              </p>
              <LevelPreview level={levels[i]} />
            </div>
          ))}
        </div>

        {planProblem && <Problem>{planProblem}</Problem>}

        {build.isPending && (
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Building {unitLabel}…
          </p>
        )}
        {/* No claim about partial creation: `buildLayout` validates the whole
            plan before opening its transaction, and the build itself is one
            transaction — a layout either exists or it does not. */}
        {build.error && <Problem>{String(build.error.message)} — nothing was created.</Problem>}

        <div className='flex items-center justify-between gap-2 border-t pt-4'>
          <Button variant='ghost' disabled={build.isPending} onClick={() => setReviewing(false)}>
            <ChevronLeft className='mr-1 h-4 w-4' />
            Back
          </Button>
          <Button
            className='gap-2'
            disabled={build.isPending || !!planProblem}
            onClick={() =>
              build.mutate(
                // A copy brings the layout only, which is already in `levels`.
                { rootId: root.id, levels },
                { onSuccess: () => onDone() },
              )
            }
          >
            <Check className='h-4 w-4' />
            Build {unitLabel}
          </Button>
        </div>
      </div>
    )
  }

  const childWord = canGoDeeper ? kindWord(childKindOptions(current.kind)[0], 1) : ''

  return (
    <div className='space-y-5'>
      {!embedded && (
        <StepHeader
          title={`Set up ${unitLabel}`}
          subtitle={
            extending
              ? 'Anything that already exists is left alone; only what is missing gets created.'
              : 'Describe it one level at a time. Nothing is created until you confirm.'
          }
        />
      )}

      {/* Offered on the FIRST level only. Once somebody is three levels into
          describing a unit by hand, replacing the whole plan is not a thing
          they are asking for, and the control would read as one. `extending`
          is excluded for the same reason: that pass fills gaps in a tree that
          already exists. */}
      {index === 0 && !extending && (
        <CopyLayoutPicker
          excludeRootId={root.id}
          onSourceLevels={applyCopiedLevels}
        />
      )}

      <Blueprint
        unitLabel={unitLabel}
        rows={rows}
        address={address}
        stored={stored}
        onGoTo={goToLevel}
        editing={index}
      />

      <div className='space-y-3'>
        <p className='text-sm font-medium'>
          {index === 0 ? `What goes inside ${unitLabel}?` : `And what goes inside ${parentLabel}?`}
        </p>
        <div className='flex flex-wrap gap-2'>
          {childKindOptions(parentKind).map((kind) => (
            <Button
              key={kind}
              type='button'
              size='sm'
              variant={current.kind === kind ? 'default' : 'outline'}
              onClick={() => setCurrent(defaultLevelFor(kind))}
            >
              {kindWord(kind, 2)}
            </Button>
          ))}
        </div>
      </div>

      {current.kind === 'SECTION' ? (
        <SectionNames level={current} onChange={setCurrent} />
      ) : (
        <GridCounts level={current} parentLabel={parentLabel} onChange={setCurrent} />
      )}

      {/* The refusals sit ABOVE the preview. A 19 × 19 grid is 361 cells tall,
          and with the reason underneath it the box turned red with its
          explanation scrolled off the bottom — the author saw that something
          was wrong and not what. */}
      {problem ? (
        <Problem>{problem}</Problem>
      ) : (
        <>
          {planProblem && <Problem>{planProblem}</Problem>}
          <div className='rounded-md border bg-muted/30 p-3'>
            <LevelPreview level={current} />
          </div>
        </>
      )}

      <div className='flex flex-wrap items-center justify-between gap-2 border-t pt-4'>
        <Button
          variant='ghost'
          onClick={() => {
            if (index === 0) onCancel()
            else setLevels((prev) => prev.slice(0, -1))
          }}
        >
          <ChevronLeft className='mr-1 h-4 w-4' />
          {index === 0 ? 'Cancel' : 'Back'}
        </Button>

        {/* One primary path. Going deeper is what people almost always do next,
            and it is named after the thing it will ask about — two identically
            weighted buttons called "Add what goes inside" and "Review" made the
            common action indistinguishable from the way out. */}
        <div className='flex items-center gap-3'>
          {canGoDeeper && (
            <button
              type='button'
              disabled={!!problem || !!planProblem}
              onClick={() => setReviewing(true)}
              className='text-sm text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50'
            >
              Nothing goes inside a {kindWord(current.kind, 1)}
            </button>
          )}
          <Button
            disabled={!!problem || !!planProblem}
            onClick={() => {
              if (!canGoDeeper) return setReviewing(true)
              setLevels((prev) => [...prev, defaultLevelFor(childKindOptions(current.kind)[0])])
            }}
          >
            {canGoDeeper ? `Add ${childWord}s` : `Review ${holds}`}
            <ChevronRight className='ml-1 h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}

function StepHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
      <p className='text-xs text-muted-foreground'>{subtitle}</p>
    </div>
  )
}

/** A refusal, in the one place the eye goes first. */
function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p className='rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
      {children}
    </p>
  )
}

/**
 * The unit as described so far, and the address it produces.
 *
 * Shown on every screen, including the last. It replaces a row of numbered
 * step chips, which said how deep you were but not what you had said — the
 * shape of the thing is the information, so the indentation IS the structure
 * rather than a decoration on top of it.
 *
 * The address at the bottom is the payoff of the whole exercise: "ten racks of
 * ten boxes" is arithmetic, and `LN2-01 / R05 / C05 / E5` is what an operator
 * reads off a label and walks to.
 */
function Blueprint({
  unitLabel,
  rows,
  address,
  stored,
  onGoTo,
  editing,
}: {
  unitLabel: string
  rows: BlueprintRow[]
  address: string
  /** Rows the build will write — racks and boxes, never slots. */
  stored: number
  onGoTo: (index: number) => void
  /** Which level is being answered right now, if any. */
  editing?: number
}) {
  // Slots cost nothing to create — a box describes its own grid — so the number
  // that bounds one request is the container count, and it is worth naming only
  // as it approaches the ceiling. Below that it is an implementation detail and
  // saying it invites the question "is 10,100 a lot?", which it is not.
  const nearLimit = stored > MAX_LAYOUT_NODES / 2
  return (
    <div className='rounded-lg border bg-muted/20 p-3'>
      <p className='text-sm font-semibold text-foreground'>{unitLabel}</p>

      <ul className='mt-1'>
        {rows.map((row, i) => {
          const isEditing = editing === i
          return (
            <li key={i} style={{ paddingLeft: `${i * 14}px` }}>
              <button
                type='button'
                onClick={() => onGoTo(i)}
                className={cn(
                  'group flex w-full items-baseline gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors',
                  isEditing ? 'bg-primary/10' : 'hover:bg-muted',
                )}
              >
                <span className='select-none text-muted-foreground'>└</span>
                <span className={cn('font-medium', isEditing && 'text-primary')}>
                  {row.total.toLocaleString()} {kindWord(row.kind, row.total)}
                </span>
                {i > 0 && (
                  <span className='text-xs text-muted-foreground'>
                    {row.perParent} per {kindWord(rows[i - 1].kind, 1)}
                  </span>
                )}
                {row.firstCode && (
                  <span className='ml-auto shrink-0 font-mono text-xs text-muted-foreground'>
                    {row.lastCode ? `${row.firstCode}…${row.lastCode}` : row.firstCode}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ul>

      {rows.length > 0 && (
        <div className='mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t pt-2 text-xs text-muted-foreground'>
          <p>
            A vial here is addressed <span className='font-mono text-foreground'>{address}</span>
          </p>
          {nearLimit && (
            <p className='tabular-nums'>
              {stored.toLocaleString()} of {MAX_LAYOUT_NODES.toLocaleString()} containers
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Sections are named, not counted — a compartment is called "Vapor". */
function SectionNames({
  level,
  onChange,
}: {
  level: Extract<WizardLevel, { kind: 'SECTION' }>
  onChange: (next: WizardLevel) => void
}) {
  const [raw, setRaw] = useState(level.names.join('\n'))
  return (
    <div className='space-y-1.5'>
      <Label htmlFor='section-names'>Name each section, one per line</Label>
      <Textarea
        id='section-names'
        rows={3}
        value={raw}
        placeholder={'Vapor\nLiquid'}
        onChange={(e) => {
          setRaw(e.target.value)
          onChange({ kind: 'SECTION', names: parseSectionNames(e.target.value) })
        }}
      />
      <p className='text-xs text-muted-foreground'>
        Sections get real names because that is what they are called on the floor.
      </p>
    </div>
  )
}

/**
 * How many, arranged how.
 *
 * Both dimensions are asked for because the shape is real — a 2 × 5 rack and a
 * 5 × 2 rack hold the same ten boxes and look nothing alike on a slot map. The
 * result is stated as a full sentence naming the parent, because the answer
 * applies to EVERY parent and that is the part that multiplies.
 */
function GridCounts({
  level,
  parentLabel,
  onChange,
}: {
  level: GridLevel
  parentLabel: string
  onChange: (next: WizardLevel) => void
}) {
  const [advanced, setAdvanced] = useState(false)
  const count = levelCount(level)

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-end gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='wiz-rows'>Rows</Label>
          <Input
            id='wiz-rows'
            className='w-20'
            type='number'
            min={1}
            value={level.rows}
            aria-invalid={dimensionProblem(level.rows) !== null}
            aria-describedby='wiz-rows-problem'
            onChange={(e) => onChange({ ...level, rows: Number(e.target.value) })}
          />
          <FieldProblem id='wiz-rows-problem' message={dimensionProblem(level.rows)} />
        </div>
        <span className='pb-2.5 text-muted-foreground'>×</span>
        <div className='space-y-1.5'>
          <Label htmlFor='wiz-columns'>Columns</Label>
          <Input
            id='wiz-columns'
            className='w-20'
            type='number'
            min={1}
            value={level.columns}
            aria-invalid={dimensionProblem(level.columns) !== null}
            aria-describedby='wiz-columns-problem'
            onChange={(e) => onChange({ ...level, columns: Number(e.target.value) })}
          />
          <FieldProblem id='wiz-columns-problem' message={dimensionProblem(level.columns)} />
        </div>
        <p className='pb-2.5 text-sm'>
          <span className='font-semibold tabular-nums'>{count.toLocaleString()}</span>{' '}
          {kindWord(level.kind, count)} in {parentLabel}
        </p>
      </div>

      <button
        type='button'
        className='text-xs text-muted-foreground underline-offset-2 hover:underline'
        onClick={() => setAdvanced((v) => !v)}
      >
        {advanced ? '▾' : '▸'} Change how they are named
      </button>

      {advanced && (
        <div className='rounded-md border p-3'>
          <LabelSchemePicker
            rows={level.rows}
            columns={level.columns}
            childKind={toNodeKind(level.kind)}
            scheme={level.scheme}
            prefix={level.prefix}
            onChange={({ scheme, prefix }) => onChange({ ...level, scheme, prefix })}
          />
        </div>
      )}
    </div>
  )
}

/** The message under one field, kept in the layout so the fields do not jump. */
function FieldProblem({ id, message }: { id: string; message: string | null }) {
  return (
    <p id={id} role={message ? 'alert' : undefined} className='h-4 text-xs text-destructive'>
      {message}
    </p>
  )
}

/**
 * What the level will build, drawn the way it will look.
 *
 * A grid level renders as its actual matrix — the shape is the whole point, and
 * 2×5 versus 5×2 is invisible in a list of names. Named sections have no
 * geometry, so they stay a list.
 */
function LevelPreview({ level }: { level: WizardLevel }) {
  if (level.kind === 'SECTION') {
    if (level.names.length === 0) return null
    return (
      <div className='mt-2 flex flex-wrap gap-1'>
        {level.names.map((name) => (
          <span
            key={name}
            className='rounded border border-dashed border-primary/30 bg-primary/5 px-2 py-0.5 text-xs font-medium text-primary'
          >
            {name}
          </span>
        ))}
      </div>
    )
  }

  let cells: GridCellLabel[]
  try {
    cells = gridCellLabels({
      rows: level.rows,
      columns: level.columns,
      scheme: level.scheme,
      prefix: level.prefix,
    })
  } catch {
    return null
  }

  return (
    <MatrixPreview
      className='mt-2'
      rows={level.rows}
      columns={level.columns}
      cells={cells}
      scheme={level.scheme}
    />
  )
}
